import { useEffect, useState } from "react";
import { arDigits, fmtLocaleNum } from "@/lib/utils";
import { useCurrency } from "@/context/CurrencyContext";

// Active display currency for every money figure in Market + Compare (values arrive in AED).
let CUR = { code: "AED", rate: 1 };
const SYM = { AED: ["AED ", "درهم"], USD: ["$", "دولار"], EUR: ["€", "يورو"], GBP: ["£", "جنيه إسترليني"], INR: ["₹", "روبية"] };
export function useIntelCurrency() {
  const { currency, rates } = useCurrency();
  CUR = { code: currency, rate: rates?.[currency] ?? 1 };
  return currency;
}
export const toCur = (v) => (v == null ? null : v * CUR.rate);
const sym = (isAR) => (SYM[CUR.code] || [`${CUR.code} `, CUR.code])[isAR ? 1 : 0];
export const fmtPsf = (v, isAR) => {
  if (v == null) return "—";
  const n = Math.round(toCur(v)).toLocaleString("en");
  return isAR ? `${arDigits(n)} ${sym(true)}` : `${sym(false)}${n}`;
};

export const N = (n, isAR) => fmtLocaleNum(n, isAR);
export const rowName = (r, isAR) => (isAR ? r.name_ar || r.name : r.name);
export const memberName = (m, isAR) => {
  if (isAR) return m.area_ar || m.area;
  const s = m.area;
  return s === s.toUpperCase() ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()).replace(/ (Of|And|The) /g, (w) => w.toLowerCase()) : s;
};

export const fmtMonth = (ym, isAR, short = false) => {
  const s = new Intl.DateTimeFormat(isAR ? "ar-AE" : "en-GB", { month: short ? "short" : "long", year: short ? "2-digit" : "numeric", timeZone: "UTC" })
    .format(new Date(`${ym}-01T00:00:00Z`));
  return isAR ? arDigits(s) : s;
};

export const fmtAED = (v, isAR) => {
  if (v == null) return "—";
  const c = toCur(v);
  const [n, uEn, uAr] = c >= 1e9 ? [c / 1e9, "B", "مليار"] : c >= 1e6 ? [c / 1e6, "M", "مليون"] : [c / 1e3, "K", "ألف"];
  const num = n.toFixed(n >= 100 ? 0 : n >= 10 ? 1 : 2);
  return isAR ? `${arDigits(num)} ${uAr} ${sym(true)}` : `${sym(false)}${num}${uEn}`;
};

export const fmtPct = (p, isAR) => {
  if (p == null || !Number.isFinite(p)) return "—";
  const s = `${p > 0 ? "+" : ""}${p.toFixed(1)}`;
  return isAR ? `\u2066${arDigits(s)}٪\u2069` : `${s}%`;
};

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 96, behavior: "smooth" });
};

export const momPct = (r, months, ym) => {
  const i = months.indexOf(ym);
  if (i < 1) return null;
  const prev = r.counts[months[i - 1]];
  return prev > 0 ? ((r.counts[ym] - prev) / prev) * 100 : null;
};

export const CELL = {
  base: { background: "#F6F1E6", color: "#14181F" },
  best: { background: "#0A2540", color: "#FFFFFF" },
  partial: { background: "#FFFFFF", color: "#6B665D", boxShadow: "inset 0 0 0 1px #D6CCB6" },
  empty: { background: "#FBF9F4", color: "#8A857B" },
};

// The single highest month for one area within the months on screen (in-progress month excluded).
export const bestMonth = (metric, r, months, allMonths, partialMonth) => {
  let best = null;
  let bestV = -Infinity;
  months.forEach((m) => {
    if (m === partialMonth) { return; }
    const v = metricValue(metric, r, allMonths, m);
    if (v != null && v > bestV) { bestV = v; best = m; }
  });
  return metric === "change" || bestV > 0 ? best : null;
};

export const cellStyle = (v, isBest, isPartial) => {
  if (isPartial) return CELL.partial;
  if (v == null) return CELL.empty;
  return isBest ? CELL.best : CELL.base;
};

export const metricValue = (metric, r, months, ym) =>
  metric === "count" ? r.counts[ym] : metric === "value" ? r.values[ym] : momPct(r, months, ym);

export const fmtMetric = (metric, v, isAR) =>
  metric === "count" ? N(v, isAR) : metric === "value" ? (v > 0 ? fmtAED(v, isAR) : N(0, isAR)) : fmtPct(v, isAR);

export const fmtDay = (d, isAR) => {
  const s = new Intl.DateTimeFormat(isAR ? "ar-AE" : "en-GB", { weekday: "long", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${d}T00:00:00Z`));
  return isAR ? arDigits(s) : s;
};

export function useIsMobile() {
  const q = "(max-width: 767px)";
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
}

export const TOP = 20;

export const T = {
  en: {
    kicker: "Dubai Market · DLD registered sales",
    title: "Market activity across 44 Dubai areas",
    intro: "Month-by-month registered property sales in The Propertic's 44 curated Dubai areas, built from official Dubai Land Department records.",
    tabs: ["Monthly activity", "Where the money goes", "Price movement"],
    matrix: "Monthly area activity",
    latest: (m) => `Latest complete month: ${m}`,
    metric: "Metric", search: "Search the 44 areas (e.g. JVC, Business Bay)", searchShort: "Search areas",
    metrics: { count: "Sales count", value: "Sales value", change: "Monthly change" },
    area: "Area", best: "Highest month for the area (in view)", other: "Other months", partialKey: "Month in progress (partial data)", inProgress: "In progress",
    changeNote: (m) => `Monthly change starts in ${m}: the DLD dataset we hold begins on 1 January 2026, so January has no earlier month to compare with.`,
    legendNote: "Highlighting marks the strongest recorded month for each area only. It does not indicate price growth or investment quality.",
    showing: (a, b) => `Showing ${a} of ${b} areas`, more: (n) => `Show all ${n} areas`, less: "Show top 20 only", noMatch: "No area matches your search.",
    coverage: (p, m) => `These 44 areas account for ${p} of all registered sales in ${m}.`,
    loading: "Loading DLD market activity…", unavailable: "Market activity data is temporarily unavailable.",
    rank: "Rank", tapHint: "Tap an area for details",
  },
  ar: {
    kicker: "سوق دبي · المبيعات المسجّلة لدى الدائرة",
    title: "نشاط السوق في ٤٤ منطقة في دبي",
    intro: "مبيعات العقارات المسجّلة شهراً بشهر في المناطق الـ٤٤ المختارة من بروبيرتك، بالاعتماد على سجلات دائرة الأراضي والأملاك الرسمية.",
    tabs: ["النشاط الشهري", "أين تذهب الأموال", "حركة الأسعار"],
    matrix: "النشاط الشهري للمناطق",
    latest: (m) => `آخر شهر مكتمل: ${m}`,
    metric: "المؤشر", search: "ابحث في المناطق الـ٤٤ (مثل JVC أو الخليج التجاري)", searchShort: "ابحث عن منطقة",
    metrics: { count: "عدد المبيعات", value: "قيمة المبيعات", change: "التغيّر الشهري" },
    area: "المنطقة", best: "أعلى شهر للمنطقة (ضمن العرض)", other: "باقي الأشهر", partialKey: "شهر قيد التحديث (بيانات جزئية)", inProgress: "قيد التحديث",
    changeNote: (m) => `يبدأ التغيّر الشهري من ${m}: تبدأ بيانات الدائرة المتوفرة لدينا في ١ يناير ٢٠٢٦، لذا لا يوجد شهر سابق لمقارنة يناير.`,
    legendNote: "يُبرز التمييز أعلى شهر مسجّل لكل منطقة فقط، ولا يدلّ على نمو الأسعار أو جودة الاستثمار.",
    showing: (a, b) => `عرض ${arDigits(a)} من ${arDigits(b)} منطقة`, more: (n) => `اعرض كل المناطق الـ${arDigits(n)}`, less: "اعرض أعلى ٢٠ فقط", noMatch: "لا توجد منطقة مطابقة لبحثك.",
    coverage: (p, m) => `تمثّل هذه المناطق الـ٤٤ ${p} من جميع المبيعات المسجّلة في ${m}.`,
    loading: "جارٍ تحميل نشاط السوق…", unavailable: "بيانات نشاط السوق غير متاحة مؤقتاً.",
    rank: "الترتيب", tapHint: "اضغط على منطقة لعرض التفاصيل",
  },
};
